import React, { useState, useEffect } from 'react';
import { Flex, Text, Image, Grid, GridItem, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, DrawerBody, DrawerHeader, Card, CardBody, Select } from '@chakra-ui/react';
import MyDiv from './leaveRequest.style';
import { LeaveList } from '../components';
import LeaveEmployeeCard from '../components/LeaveEmployeeCard';
import LeaveRequestIcon from '../../../assets/images/LeaveRequestIcon.svg';
import PendingApprovalsIcon from '../../../assets/images/PendingApprovalsIcon.svg';
import RejectRequestsIcon from '../../../assets/images/RejectedRequestsIcon.svg';
import ApprovedLeavesIcon from '../../../assets/images/ApprovedLeavesIcon.svg';
import OnLeaveIcon from '../../../assets/images/OnLeaveIcon.svg';
import { CustomToast } from '../../../components';
import { ILazyParams, useFetch } from '../../../hooks/useFetch';
import { ILeaveRequest } from './modal';
import { IFilterOptions } from '../../../components/FilterCard/filterCard';
import DateRangePicker from '../../../components/DateRangePicker/DateRangePicker';


type LeaveLazyParams = ILazyParams & { filters?: Record<string, any> };

const LeaveRequest = () => {
  const [leaveData, setLeaveData] = useState<any[]>([]);
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const [selectedLeave, setSelectedLeave] = useState<ILeaveRequest | null>(null);
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [leaveList, setLeaveList] = useState<{ data: ILeaveRequest[], totalCount: number }>({ data: [], totalCount: 0, });
  const [reload, setReload] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });

  const [query, setQuery] = useState<{ lazyParams: LeaveLazyParams, filter: Record<string, (string | number | undefined)>, }>({
    lazyParams: { first: 0, rows: 20, page: 0, sortField: '', sortOrder: 1, filters: {} },
    filter: {},
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setQuery((prev) =>
        prev.lazyParams.first === 0
          ? prev
          : { ...prev, lazyParams: { ...prev.lazyParams, first: 0, page: 0 } }
      );
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const iconMap: Record<string, string> = {
    'Total Leave Requests': LeaveRequestIcon,
    'Pending Approvals': PendingApprovalsIcon,
    'Approved Leaves': ApprovedLeavesIcon,
    'Rejected Requests': RejectRequestsIcon,
    'On Leave Today': OnLeaveIcon
  };

  const loadLeaveList = async () => {
    const { startDate, endDate } = query.filter as any;
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return addToast({
        message: 'Please select both start and end date.',
        status: 'warning',
      });
    }

    const params = new URLSearchParams();
    params.set('first', String(query.lazyParams.first ?? 0));
    params.set('rows', String(query.lazyParams.rows));
    if (query.lazyParams.sortField) params.set('sortField', query.lazyParams.sortField);
    if (query.lazyParams.sortOrder) params.set('sortOrder', String(query.lazyParams.sortOrder));
    if (debouncedSearch) params.set('globalFilter', debouncedSearch);

    const columnFilters = query.lazyParams.filters;
    if (columnFilters && Object.keys(columnFilters).length) {
      params.set('filters', JSON.stringify(columnFilters));
    }

    params.set('filter', JSON.stringify(query.filter));

    const response = await fetchApi('Hrlense_Leave/LeaveRequests', 'GET', null, params.toString(), '');
    if (response) {
      setLeaveList({ data: response.data ?? [], totalCount: response.totalRecords ?? 0 });

      const updated = (response.summary ?? []).map((item: any) => ({
        ...item,
        icon: iconMap[item.title] || null
      }));
      setLeaveData(updated);
    }
  };

  const loadFilter = async () => {
    const response = await fetchApi("Filters/LeaveRequestFilter", "GET", null, null, '');
    if (response) {
      setFilter(response);
    }
  };


  useEffect(() => {
    loadFilter();
  }, []);

  useEffect(() => {
    loadLeaveList();
  }, [reload, query, debouncedSearch]);

  const handleViewLeave = (leave: any) => {
    setSelectedLeave(leave);
    onDrawerOpen();
  };

  const handleCardClick = (card: any) => {
    let newFilter: any = { ...query.filter };
    switch (card.title) {
      case "Pending Approvals":
        newFilter.status = [1];
        delete newFilter.onLeaveToday;
        break;
      case "Approved Leaves":
        newFilter.status = [2];
        delete newFilter.onLeaveToday;
        break;
      case "Rejected Requests":
        newFilter.status = [3];
        delete newFilter.onLeaveToday;
        break;
      case "On Leave Today":
        delete newFilter.status;
        newFilter.onLeaveToday = true;
        break;
      case "Total Leave Requests":
        delete newFilter.status;
        delete newFilter.onLeaveToday;
        break;
      default:
        break;
    }

    setQuery((prev) => ({
      ...prev,
      filter: newFilter,
      lazyParams: { ...prev.lazyParams, first: 0, page: 0 },
    }));
  };

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <MyDiv>
      <Flex justifyContent="space-between" alignItems="center" mb={1} p={1}>
        <Text className="font-poppins font_dark text_semibold text_4xl" mt={3}>Leaves</Text>
        <Flex>
          <DateRangePicker
            onChange={(dateRange) => {
              if (dateRange && dateRange.start && dateRange.end) {
                setQuery((prev) => ({
                  ...prev,
                  filter: {
                    ...prev.filter,
                    startDate: formatLocalDate(dateRange.start),
                    endDate: formatLocalDate(dateRange.end),
                  },
                  lazyParams: { ...prev.lazyParams, first: 0, page: 0 },
                }));
              }

              if (!dateRange) {
                setQuery((prev) => {
                  const updatedFilter = { ...prev.filter };
                  delete updatedFilter.startDate;
                  delete updatedFilter.endDate;

                  return {
                    ...prev,
                    filter: updatedFilter,
                    lazyParams: { ...prev.lazyParams, first: 0, page: 0 },
                  };
                });
              }
            }}
          />

          <Select width="160px" height="35px" ml={3} placeholder="Filter By" className="font-poppins text_sm text_regular" onChange={(e: any) => {
            const value = e.target.value;
            let start: any, end: any;
            const today = new Date();
            if (value === "week") {
              const now = new Date();
              const first = now.getDate() - now.getDay(); // Sunday

              start = new Date(now.getFullYear(), now.getMonth(), first);
              end = new Date(now.getFullYear(), now.getMonth(), first + 6);
            }
            else if (value === "month") {
              start = new Date(today.getFullYear(), today.getMonth(), 1);
              end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            }
            else if (value === "quarter") {
              const quarter = Math.floor(today.getMonth() / 3);
              start = new Date(today.getFullYear(), quarter * 3, 1);
              end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
            }
            else if (value === "year") {
              start = new Date(today.getFullYear(), 0, 1);
              end = new Date(today.getFullYear(), 11, 31);
            }
            else {
              setQuery((prev) => ({
                ...prev,
                filter: {},
                lazyParams: { ...prev.lazyParams, first: 0, page: 0 },
              }));
              return;
            }

            if (start && end) {
              setQuery((prev) => ({
                ...prev,
                filter: {
                  ...prev.filter,
                  startDate: formatLocalDate(start),
                  endDate: formatLocalDate(end),
                },
                lazyParams: { ...prev.lazyParams, first: 0, page: 0 },
              }));
            }
          }}
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </Select>
        </Flex>
      </Flex>

      <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }} gap={4} className="card_box" >
        {leaveData.map((data, index) => (
          <GridItem key={index} colSpan={1}>
            <Card className="card" cursor="pointer" onClick={() => handleCardClick(data)}>
              <CardBody p={4}>
                <Flex direction="column">
                  <Flex alignItems="center">
                    {data.icon && <Image src={data.icon} alt={data.title} mr={2} />}
                    <Text className="font-poppins text_xl text_medium">{data.title}</Text>
                  </Flex>
                  {data.value !== undefined && (
                    <Text className="font-poppins text_5xl text_semibold" p={2}>{data.value}</Text>
                  )}
                </Flex>
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>

      <Grid className="grid_container" mt={1}>
        <GridItem colSpan={12}>
          <LeaveList leaveList={leaveList} loading={loading} onView={handleViewLeave} onReload={() => setReload(prev => !prev)} query={query} setQuery={setQuery} setSearch={setSearch} filter={filter} />
        </GridItem>
      </Grid>

      <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Employee Leave Application</DrawerHeader>
          <DrawerBody>
            <LeaveEmployeeCard leaveData={selectedLeave} onClose={onDrawerClose} onReload={() => setReload(prev => !prev)} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </MyDiv>
  );
};

export default LeaveRequest;